/**
 * useAcademyChat — message state machine for the Academy AI Assistant.
 *
 * Must be rendered inside an <AcademyChatProvider> which sets up the Ably
 * connection and channel. This hook subscribes to Copilot streaming events
 * via useAblyChannel and exposes actions to send messages, stop a stream,
 * and reset the conversation.
 *
 * Streaming event lifecycle for a single response:
 *   copilot_response_start
 *     → copilot_response_message_start (initial section with first text chunk)
 *     → N × copilot_response_message_delta
 *     → copilot_response_message_end
 *     → copilot_response_end
 */
import Raven from 'raven-js';
import { useCallback, useRef, useState } from 'react';
import { useAblyChannel } from 'ably-hubspot-js/v3/useAblyChannel';
import { ChatRequestTypename, LlmResponseSectionTypename } from '../types';
import { parseCopilotMessage, extractTextFromSections } from './academyChatHelpers';
import { UserInterruptTypename } from '../../__generated__/com/hubspot/copilot/models/chat/ChannelEvent';
import { ABLY_ID, CHANNEL_EVENT_MODEL_FINGERPRINT } from '../consts';
import I18n from 'I18n';
const INITIAL_STATE = {
  messages: [],
  isStreaming: false,
  error: null
};
function useReasoningDuration() {
  const startTimeRef = useRef(null);
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
  }, []);
  const stopTimer = useCallback(() => {
    if (startTimeRef.current == null) return undefined;
    const elapsedMs = Date.now() - startTimeRef.current;
    startTimeRef.current = null;
    const seconds = Math.round(elapsedMs / 1000);
    return seconds > 0 ? seconds : undefined;
  }, []);
  const resetTimer = useCallback(() => {
    startTimeRef.current = null;
  }, []);
  return {
    startTimer,
    stopTimer,
    resetTimer
  };
}
export function useAcademyChat({
  channelName,
  systemConfig,
  initialPrompt
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const stoppedRef = useRef(false);
  const interactionIdRef = useRef(null);
  // Direct reference to the message being streamed so we can mutate it
  // in place without copying the messages array on every delta.
  // Also serves as the non-stale streaming guard (non-null === streaming).
  const streamingMessageRef = useRef(null);
  const {
    startTimer: startReasoningTimer,
    stopTimer: stopReasoningTimer,
    resetTimer: resetReasoningTimer
  } = useReasoningDuration();
  const isCurrentlyStreaming = () => streamingMessageRef.current !== null;
  const handleMessage = useCallback(copilotMessage => {
    try {
      if (stoppedRef.current && copilotMessage.name !== 'copilot_response_start') {
        return;
      }
      const {
        data
      } = copilotMessage;
      switch (copilotMessage.name) {
        case 'copilot_response_start':
          {
            var _responseStart$userMe, _responseStart$userMe2;
            stoppedRef.current = false;
            const responseStart = data;
            interactionIdRef.current = (_responseStart$userMe = (_responseStart$userMe2 = responseStart.userMessage) === null || _responseStart$userMe2 === void 0 ? void 0 : _responseStart$userMe2.interactionId) !== null && _responseStart$userMe !== void 0 ? _responseStart$userMe : null;
            if (!streamingMessageRef.current) {
              startReasoningTimer();
            }
            setState(prev => {
              if (prev.isStreaming) {
                return Object.assign({}, prev, {
                  error: null
                });
              }
              const placeholder = {
                role: 'assistant',
                content: '',
                timestamp: Date.now()
              };
              streamingMessageRef.current = placeholder;
              return Object.assign({}, prev, {
                isStreaming: true,
                messages: [...prev.messages, placeholder],
                error: null
              });
            });
            break;
          }

        // Each LlmResponseSection message start resets the accumulated content.
        // This discards any earlier "thinking" text (e.g. "I am looking for...")
        // and starts rendering the final response from scratch.
        case 'copilot_response_message_start':
          {
            const {
              section
            } = data;
            if ((section === null || section === void 0 ? void 0 : section.__typename) === LlmResponseSectionTypename && streamingMessageRef.current) {
              const text = section.text || '';
              streamingMessageRef.current.content = text;
              const reasoningDurationSeconds = stopReasoningTimer();
              if (reasoningDurationSeconds != null) {
                streamingMessageRef.current.reasoningDurationSeconds = reasoningDurationSeconds;
              }
              setState(prev => {
                const lastIdx = prev.messages.length - 1;
                const last = prev.messages[lastIdx];
                if ((last === null || last === void 0 ? void 0 : last.role) !== 'assistant') return prev;
                return Object.assign({}, prev, {
                  messages: [...prev.messages.slice(0, lastIdx), Object.assign({}, last, {
                    content: text
                  }, reasoningDurationSeconds != null ? {
                    reasoningDurationSeconds
                  } : {})]
                });
              });
            }
            break;
          }
        case 'copilot_response_message_delta':
          {
            const delta = data;
            if (delta.text && streamingMessageRef.current) {
              streamingMessageRef.current.content += delta.text;
              const snapshot = streamingMessageRef.current.content;
              setState(prev => {
                const lastIdx = prev.messages.length - 1;
                const last = prev.messages[lastIdx];
                if ((last === null || last === void 0 ? void 0 : last.role) !== 'assistant') return prev;
                return Object.assign({}, prev, {
                  messages: [...prev.messages.slice(0, lastIdx), Object.assign({}, last, {
                    content: snapshot
                  })]
                });
              });
            }
            break;
          }

        // The full message payload arrives with all sections. We replace
        // the delta-accumulated text with the authoritative full text to
        // correct any potential drift from missed deltas.
        case 'copilot_response_message_end':
          {
            var _message$sections;
            const {
              message
            } = data;
            const hasLlmSection = message === null || message === void 0 || (_message$sections = message.sections) === null || _message$sections === void 0 ? void 0 : _message$sections.some(s => s.__typename === LlmResponseSectionTypename);
            if (hasLlmSection && streamingMessageRef.current) {
              const content = extractTextFromSections(message.sections);
              streamingMessageRef.current.content = content;
              streamingMessageRef.current.sections = message.sections;
              streamingMessageRef.current.dynamicSectionContainers = message.dynamicSectionContainers || [];
              setState(prev => {
                const lastIdx = prev.messages.length - 1;
                const last = prev.messages[lastIdx];
                if ((last === null || last === void 0 ? void 0 : last.role) !== 'assistant') return prev;
                return Object.assign({}, prev, {
                  messages: [...prev.messages.slice(0, lastIdx), Object.assign({}, last, {
                    content,
                    sections: message.sections,
                    dynamicSectionContainers: message.dynamicSectionContainers || []
                  })]
                });
              });
            }
            break;
          }

        // Streaming complete — just flip the flag. The message is already
        // in the messages array. The UI component handles the typewriter
        // wind-down internally.
        case 'copilot_response_end':
          {
            resetReasoningTimer();
            streamingMessageRef.current = null;
            setState(prev => {
              const last = prev.messages[prev.messages.length - 1];
              // If the backend sent start → end with no deltas, drop the empty
              // placeholder so we don't leave a ghost message in the chat.
              if ((last === null || last === void 0 ? void 0 : last.role) === 'assistant' && !last.content.trim()) {
                return Object.assign({}, prev, {
                  isStreaming: false,
                  messages: prev.messages.slice(0, -1)
                });
              }
              return Object.assign({}, prev, {
                isStreaming: false
              });
            });
            break;
          }
        case 'copilot_response_exception':
          {
            resetReasoningTimer();
            streamingMessageRef.current = null;
            const {
              message
            } = data;
            const errorMessage = (message === null || message === void 0 ? void 0 : message.errorCode) || I18n.text('academy-ai-assistant.errors.responseException');
            setState(prev => {
              const last = prev.messages[prev.messages.length - 1];
              const messages = (last === null || last === void 0 ? void 0 : last.role) === 'assistant' && !last.content.trim() ? prev.messages.slice(0, -1) : prev.messages;
              return Object.assign({}, prev, {
                isStreaming: false,
                messages,
                error: errorMessage
              });
            });
            break;
          }
        case 'copilot_tool_call_start':
          {
            const toolCall = data;
            if (toolCall.toolName === 'AcademySuggestionTools_createSuggestionButtons' && toolCall.arguments && streamingMessageRef.current) {
              try {
                const parsed = JSON.parse(toolCall.arguments);
                if (Array.isArray(parsed.suggestions)) {
                  var _streamingMessageRef$;
                  const newLabels = parsed.suggestions.filter(s => typeof s === 'string');
                  const existing = (_streamingMessageRef$ = streamingMessageRef.current.suggestionLabels) !== null && _streamingMessageRef$ !== void 0 ? _streamingMessageRef$ : [];
                  const merged = [...existing, ...newLabels];
                  streamingMessageRef.current.suggestionLabels = merged;
                  setState(prev => {
                    const lastIdx = prev.messages.length - 1;
                    const last = prev.messages[lastIdx];
                    if ((last === null || last === void 0 ? void 0 : last.role) !== 'assistant') return prev;
                    return Object.assign({}, prev, {
                      messages: [...prev.messages.slice(0, lastIdx), Object.assign({}, last, {
                        suggestionLabels: merged
                      })]
                    });
                  });
                }
              } catch (_unused) {
                // malformed arguments JSON — ignore
              }
            }
            break;
          }
        case 'thread_title_update':
        case 'user_message':
        case 'user_action':
        case 'memory_update':
        case 'copilot_status_update':
        case 'copilot_tool_call_end':
        case 'copilot_response_message_update':
          break;
        default:
          break;
      }
    } catch (err) {
      Raven.captureException(err instanceof Error ? err : new Error(String(err)));
    }
  }, [startReasoningTimer, stopReasoningTimer, resetReasoningTimer]);
  const handlePublishError = useCallback(errorMessage => {
    resetReasoningTimer();
    streamingMessageRef.current = null;
    setState(prev => {
      const last = prev.messages[prev.messages.length - 1];
      const messages = (last === null || last === void 0 ? void 0 : last.role) === 'assistant' && !last.content.trim() ? prev.messages.slice(0, -1) : prev.messages;
      return Object.assign({}, prev, {
        isStreaming: false,
        messages,
        error: errorMessage !== null && errorMessage !== void 0 ? errorMessage : null
      });
    });
  }, [resetReasoningTimer]);
  const channelResult = useAblyChannel({
    ablyId: ABLY_ID,
    channelName,
    messageParser: parseCopilotMessage,
    onMessage: handleMessage
  });
  const channel = channelResult.channel;

  // Publishes a ChatRequest to the Ably channel. The Copilot backend picks
  // it up and streams back a response via the event handler above.
  const sendMessage = useCallback(prompt => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isCurrentlyStreaming()) {
      return;
    }
    resetReasoningTimer();
    const now = Date.now();
    const userMessage = {
      role: 'user',
      content: trimmedPrompt,
      timestamp: now
    };
    const assistantPlaceholder = {
      role: 'assistant',
      content: '',
      timestamp: now + 1
    };
    streamingMessageRef.current = assistantPlaceholder;
    setState(prev => Object.assign({}, prev, {
      isStreaming: true,
      messages: [...prev.messages, userMessage, assistantPlaceholder],
      error: null
    }));
    const payload = {
      __typename: ChatRequestTypename,
      message: trimmedPrompt,
      hidden: false,
      type: 'CHAT',
      context: Object.assign({
        contextItems: [],
        clientType: 'DESKTOP',
        clientSurface: 'EMBEDDED',
        locale: 'en',
        // TODO: use user locale?
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        collabMode: false,
        chirpToolFqcns: [],
        files: [],
        // TODO: file upload?
        skipMemoryProcessing: true,
        // TODO: use previous chats?
        includedDeferredChirpTools: []
      }, systemConfig ? {
        systemConfig
      } : {})
    };
    channel.publish('user_message', {
      chirpMessageJson: JSON.stringify(payload),
      chirpFingerprint: CHANNEL_EVENT_MODEL_FINGERPRINT
    }, error => {
      if (error) {
        handlePublishError(I18n.text('academy-ai-assistant.errors.sendMessage'));
      }
    });
  }, [channel, systemConfig, handlePublishError, resetReasoningTimer]);
  const sentInitialPrompt = useRef(false);
  if (initialPrompt && !sentInitialPrompt.current) {
    sentInitialPrompt.current = true;
    channel.whenState('attached').then(() => sendMessage(initialPrompt)).catch(error => {
      Raven.captureException(error);
    });
  }
  const stopStream = useCallback(() => {
    const interactionId = interactionIdRef.current;
    stoppedRef.current = true;
    resetReasoningTimer();
    streamingMessageRef.current = null;
    interactionIdRef.current = null;
    setState(prev => Object.assign({}, prev, {
      isStreaming: false
    }));
    if (interactionId) {
      channel.publish('user_interrupt', {
        chirpMessageJson: JSON.stringify({
          __typename: UserInterruptTypename,
          id: interactionId
        }),
        chirpFingerprint: CHANNEL_EVENT_MODEL_FINGERPRINT
      }, error => {
        if (error) {
          handlePublishError();
        }
      });
    }
  }, [channel, handlePublishError, resetReasoningTimer]);
  const resetChat = useCallback(() => {
    resetReasoningTimer();
    streamingMessageRef.current = null;
    setState(INITIAL_STATE);
  }, [resetReasoningTimer]);
  return Object.assign({}, state, {
    sendMessage,
    stopStream,
    resetChat
  });
}